import { getLastLoopPosition } from './LoopPositionStorage';

interface BracketPlacementInput {
  songId: string;
  cursorStartTimestamp: number | null;
  firstPhraseEndOffset: number | null;
  timeSignatureNumerator: number;
  timeSignatureDenominator: number;
  ticksPerQuarterNote: number;
  allNoteTimestamps: number[];
  allNoteOffsets: number[];
}

interface BracketPlacementResult {
  startTimestamp: number;
  endTimestamp: number;
}

const MINIMUM_NOTES_IN_BRACKET = 2;
const BARS_FOR_DEFAULT_BRACKET = 4;

function isValidNonEmptyNumberArray(value: unknown): value is number[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => typeof item === 'number' && !Number.isNaN(item))
  );
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value) && value >= 0;
}

/**
 * Binary search to find the index of the element in a sorted array
 * that is closest to the target value.
 */
function findNearestIndex(sortedValues: number[], target: number): number {
  if (sortedValues.length === 0) {
    throw new Error('Cannot find nearest index in an empty array');
  }

  if (sortedValues.length === 1) {
    return 0;
  }

  let low = 0;
  let high = sortedValues.length - 1;

  while (low < high - 1) {
    const mid = Math.floor((low + high) / 2);

    if (sortedValues[mid] === target) {
      return mid;
    }

    if (sortedValues[mid] < target) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const distanceToLow = Math.abs(sortedValues[low] - target);
  const distanceToHigh = Math.abs(sortedValues[high] - target);

  return distanceToLow <= distanceToHigh ? low : high;
}

/**
 * Snap a timestamp to the nearest note onset using binary search.
 */
function snapToNearestNoteOnset(timestamp: number, noteOnsets: number[]): number {
  if (!isNonNegativeNumber(timestamp)) {
    throw new TypeError('timestamp must be a non-negative number');
  }

  if (!isValidNonEmptyNumberArray(noteOnsets)) {
    throw new TypeError('noteOnsets must be a non-empty array of numbers');
  }

  const nearestIndex = findNearestIndex(noteOnsets, timestamp);
  return noteOnsets[nearestIndex];
}

/**
 * Snap a timestamp to the nearest note offset using binary search.
 */
function snapToNearestNoteOffset(timestamp: number, noteOffsets: number[]): number {
  if (!isNonNegativeNumber(timestamp)) {
    throw new TypeError('timestamp must be a non-negative number');
  }

  if (!isValidNonEmptyNumberArray(noteOffsets)) {
    throw new TypeError('noteOffsets must be a non-empty array of numbers');
  }

  const nearestIndex = findNearestIndex(noteOffsets, timestamp);
  return noteOffsets[nearestIndex];
}

/**
 * Calculate the tick duration of a given number of bars based on time signature
 * and ticks per quarter note.
 *
 * Formula: bars * beatsPerBar * ticksPerBeat
 * where ticksPerBeat = ticksPerQuarterNote * (4 / denominator)
 *
 * For 4/4 time at 480 tpqn: 4 * 4 * 480 = 7680 ticks per 4 bars
 * For 3/4 time at 480 tpqn: 4 * 3 * 480 = 5760 ticks per 4 bars
 * For 6/8 time at 480 tpqn: 4 * 6 * 240 = 5760 ticks per 4 bars
 */
function calculateBarsDurationInTicks(
  barCount: number,
  timeSignatureNumerator: number,
  timeSignatureDenominator: number,
  ticksPerQuarterNote: number,
): number {
  const ticksPerBeat = ticksPerQuarterNote * (4 / timeSignatureDenominator);
  return barCount * timeSignatureNumerator * ticksPerBeat;
}

/**
 * Count how many note onsets fall between startTimestamp (inclusive) and
 * endTimestamp (inclusive) using binary search.
 */
function countNotesInRange(
  noteOnsets: number[],
  startTimestamp: number,
  endTimestamp: number,
): number {
  if (noteOnsets.length === 0) {
    return 0;
  }

  const firstIndex = findFirstIndexAtOrAfter(noteOnsets, startTimestamp);
  const lastIndex = findLastIndexAtOrBefore(noteOnsets, endTimestamp);

  if (firstIndex === -1 || lastIndex === -1 || firstIndex > lastIndex) {
    return 0;
  }

  return lastIndex - firstIndex + 1;
}

/**
 * Find the index of the first element >= target using binary search.
 * Returns -1 if no element is >= target.
 */
function findFirstIndexAtOrAfter(sortedValues: number[], target: number): number {
  let low = 0;
  let high = sortedValues.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    if (sortedValues[mid] >= target) {
      result = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  return result;
}

/**
 * Find the index of the last element <= target using binary search.
 * Returns -1 if no element is <= target.
 */
function findLastIndexAtOrBefore(sortedValues: number[], target: number): number {
  let low = 0;
  let high = sortedValues.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    if (sortedValues[mid] <= target) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return result;
}

/**
 * Extend the end timestamp until the bracket contains at least the minimum
 * number of notes. Walks forward through note offsets to find a valid end.
 */
function ensureMinimumNotes(
  startTimestamp: number,
  endTimestamp: number,
  noteOnsets: number[],
  noteOffsets: number[],
): number {
  const noteCount = countNotesInRange(noteOnsets, startTimestamp, endTimestamp);

  if (noteCount >= MINIMUM_NOTES_IN_BRACKET) {
    return endTimestamp;
  }

  const firstOnsetIndex = findFirstIndexAtOrAfter(noteOnsets, startTimestamp);

  if (firstOnsetIndex === -1) {
    return endTimestamp;
  }

  const minimumLastNoteIndex = firstOnsetIndex + MINIMUM_NOTES_IN_BRACKET - 1;

  if (minimumLastNoteIndex >= noteOnsets.length) {
    return noteOffsets[noteOffsets.length - 1];
  }

  const minimumLastNoteOnset = noteOnsets[minimumLastNoteIndex];
  return snapToNearestNoteOffset(minimumLastNoteOnset, noteOffsets);
}

/**
 * Snap a timestamp to the nearest note offset that does not exceed the target.
 * Prevents the bracket end from spilling into the next phrase when the raw end
 * lands near a phrase boundary.  Falls back to the nearest offset if all
 * offsets are past the target (i.e. very short piece).
 */
function snapToNoteOffsetAtOrBefore(timestamp: number, noteOffsets: number[]): number {
  const index = findLastIndexAtOrBefore(noteOffsets, timestamp);
  if (index !== -1) return noteOffsets[index];
  // All offsets are past the target — fall back to the very first offset
  return noteOffsets[0];
}

/**
 * Build a bracket placement result with snapping and minimum note enforcement.
 *
 * The end is determined by finding the last note whose *onset* is strictly
 * before the raw end, then using that note's offset as the bracket end.
 * This prevents the bracket from including the first note of the next phrase
 * when the raw end lands exactly on a phrase boundary.
 */
function buildSnappedBracketResult(
  rawStart: number,
  rawEnd: number,
  noteOnsets: number[],
  noteOffsets: number[],
): BracketPlacementResult {
  const snappedStart = snapToNearestNoteOnset(rawStart, noteOnsets);

  // Find the last note onset that is strictly before the raw end.
  // This ensures we pick the last note *inside* the bar range, not the
  // first note of the next phrase whose onset might equal rawEnd.
  // Then find that note's offset (the first offset strictly after the onset).
  const lastOnsetIndex = findLastIndexStrictlyBefore(noteOnsets, rawEnd);
  const lastOnsetValue = lastOnsetIndex !== -1 ? noteOnsets[lastOnsetIndex] : null;
  // Find the offset of the last included note. Use findFirstIndexAtOrAfter
  // to get the first offset strictly after the onset (the note's end time),
  // avoiding the +1 buffer which can overshoot into the next phrase.
  const lastOnset = lastOnsetIndex !== -1 ? noteOnsets[lastOnsetIndex] : null;
  const snappedEnd =
    lastOnset !== null
      ? (() => {
          const idx = findFirstIndexAtOrAfter(noteOffsets, lastOnset + 0.001);
          return idx !== -1 ? noteOffsets[idx] : snapToNoteOffsetAtOrBefore(rawEnd, noteOffsets);
        })()
      : snapToNoteOffsetAtOrBefore(rawEnd, noteOffsets);

  const adjustedEnd = ensureMinimumNotes(snappedStart, snappedEnd, noteOnsets, noteOffsets);

  console.log(
    `[BRACKET-SNAP] buildSnappedBracketResult rawStart=${rawStart} rawEnd=${rawEnd} snappedStart=${snappedStart} lastOnsetIndex=${lastOnsetIndex} lastOnsetValue=${lastOnsetValue} snappedEnd=${snappedEnd} adjustedEnd=${adjustedEnd}`,
  );

  return {
    startTimestamp: snappedStart,
    endTimestamp: adjustedEnd,
  };
}

/**
 * Find the index of the last element strictly less than target.
 * Returns -1 if no element is < target.
 */
function findLastIndexStrictlyBefore(sortedValues: number[], target: number): number {
  let low = 0;
  let high = sortedValues.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    if (sortedValues[mid] < target) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return result;
}

/**
 * Determine where the loop bracket should appear when looping is first activated.
 *
 * Priority order:
 * 1. Last stored loop position (from session storage), re-snapped to note boundaries
 * 2. Cursor position + 4 bars, snapped to note boundaries
 * 3. First note + 4 bars, snapped to note boundaries
 *
 * If there are no notes in the piece, returns start=0 and end=0.
 */
function calculateDefaultBracketPlacement(input: BracketPlacementInput): BracketPlacementResult {
  validateBracketPlacementInput(input);

  const {
    songId,
    cursorStartTimestamp,
    firstPhraseEndOffset,
    timeSignatureNumerator,
    timeSignatureDenominator,
    ticksPerQuarterNote,
    allNoteTimestamps,
    allNoteOffsets,
  } = input;

  if (allNoteTimestamps.length === 0 || allNoteOffsets.length === 0) {
    return { startTimestamp: 0, endTimestamp: 0 };
  }

  const storedPosition = getLastLoopPosition(songId);

  if (storedPosition !== null) {
    console.log(
      `[BRACKET-DEFAULT] PATH: stored position start=${storedPosition.startTimestamp} end=${storedPosition.endTimestamp}`,
    );
    return buildSnappedBracketResult(
      storedPosition.startTimestamp,
      storedPosition.endTimestamp,
      allNoteTimestamps,
      allNoteOffsets,
    );
  }

  // Default to the first phrase (first line of sheet music). Fall back to
  // a 4-bar duration when phrase boundary info is unavailable.
  const fourBarsDuration = calculateBarsDurationInTicks(
    BARS_FOR_DEFAULT_BRACKET,
    timeSignatureNumerator,
    timeSignatureDenominator,
    ticksPerQuarterNote,
  );

  const firstNoteOnset = allNoteTimestamps[0];
  const rawEnd = firstPhraseEndOffset ?? firstNoteOnset + fourBarsDuration;

  console.log(
    `[BRACKET-DEFAULT] firstPhraseEndOffset=${firstPhraseEndOffset} fourBarsDuration=${fourBarsDuration} timeSig=${timeSignatureNumerator}/${timeSignatureDenominator} firstNoteOnset=${firstNoteOnset} rawEnd=${rawEnd}`,
  );

  const rawStart = cursorStartTimestamp ?? firstNoteOnset;
  console.log(`[BRACKET-DEFAULT] PATH: rawStart=${rawStart} rawEnd=${rawEnd}`);
  return buildSnappedBracketResult(rawStart, rawEnd, allNoteTimestamps, allNoteOffsets);
}

function validateBracketPlacementInput(input: BracketPlacementInput): void {
  if (typeof input !== 'object' || input === null) {
    throw new TypeError('input must be a non-null object');
  }

  if (typeof input.songId !== 'string' || input.songId.length === 0) {
    throw new TypeError('songId must be a non-empty string');
  }

  if (input.cursorStartTimestamp !== null && !isNonNegativeNumber(input.cursorStartTimestamp)) {
    throw new TypeError('cursorStartTimestamp must be a non-negative number or null');
  }

  if (input.firstPhraseEndOffset !== null && !isNonNegativeNumber(input.firstPhraseEndOffset)) {
    throw new TypeError('firstPhraseEndOffset must be a non-negative number or null');
  }

  if (!isPositiveInteger(input.timeSignatureNumerator)) {
    throw new TypeError('timeSignatureNumerator must be a positive integer');
  }

  if (!isPositiveInteger(input.timeSignatureDenominator)) {
    throw new TypeError('timeSignatureDenominator must be a positive integer');
  }

  if (
    typeof input.ticksPerQuarterNote !== 'number' ||
    input.ticksPerQuarterNote <= 0 ||
    Number.isNaN(input.ticksPerQuarterNote)
  ) {
    throw new TypeError('ticksPerQuarterNote must be a positive number');
  }

  if (!Array.isArray(input.allNoteTimestamps)) {
    throw new TypeError('allNoteTimestamps must be an array of numbers');
  }

  if (!Array.isArray(input.allNoteOffsets)) {
    throw new TypeError('allNoteOffsets must be an array of numbers');
  }
}

export type { BracketPlacementInput, BracketPlacementResult };
export {
  calculateDefaultBracketPlacement,
  snapToNearestNoteOnset,
  snapToNearestNoteOffset,
  calculateBarsDurationInTicks,
};
